import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Users, Building2 } from "lucide-react";
import { SettingsPage } from "@/components/v2/settings-page";
import { SettingsField } from "@/components/v2/settings-field";
import { SettingsTable } from "@/components/v2/settings-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAllAccounts, useCreateAccount } from "@/hooks/use-admin";
import type {
  AccountWithCounts,
  AccountDetailResponse,
} from "@/hooks/use-admin";

function statusVariant(
  status: AccountWithCounts["status"],
): "default" | "secondary" | "destructive" | "outline" {
  if (status === "active") return "default";
  if (status === "suspended") return "destructive";
  return "outline";
}

// ─── CreateAccountSheet ───────────────────────────────────────────────────────

interface CreateAccountSheetProps {
  open: boolean;
  onClose: () => void;
}

interface CreateFormState {
  accountName: string;
  accountSlug: string;
  businessName: string;
  businessSlug: string;
  adminEmail: string;
  adminFirstName: string;
  adminLastName: string;
}

const emptyForm: CreateFormState = {
  accountName: "",
  accountSlug: "",
  businessName: "",
  businessSlug: "",
  adminEmail: "",
  adminFirstName: "",
  adminLastName: "",
};

function slugify(val: string): string {
  return val
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function CreateAccountSheet({ open, onClose }: CreateAccountSheetProps) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<CreateFormState>(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [adminExisted, setAdminExisted] = useState(false);
  // ADV-1027: track whether the user has hand-edited each slug so typing in
  // the parent name field no longer clobbers a customised value.
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [businessSlugManuallyEdited, setBusinessSlugManuallyEdited] =
    useState(false);
  const createAccount = useCreateAccount();

  function patch(fields: Partial<CreateFormState>) {
    setForm((prev) => ({ ...prev, ...fields }));
  }

  function handleClose() {
    setStep(1);
    setForm(emptyForm);
    setError(null);
    setAdminExisted(false);
    setSlugManuallyEdited(false);
    setBusinessSlugManuallyEdited(false);
    onClose();
  }

  async function handleSubmit() {
    setError(null);
    setSubmitting(true);
    try {
      const result: AccountDetailResponse = await createAccount.mutateAsync({
        name: form.accountName,
        // Blank means "let the server derive one" (ADV-1027). The schema
        // accepts undefined; an empty string would still fail Zod min(1).
        slug: form.accountSlug || undefined,
        admin_email: form.adminEmail,
        admin_first_name: form.adminFirstName || undefined,
        admin_last_name: form.adminLastName || undefined,
        first_business_name: form.businessName || undefined,
        first_business_slug: form.businessSlug || undefined,
      });
      if (result.admin_existed) {
        setAdminExisted(true);
      } else {
        handleClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create account");
    } finally {
      setSubmitting(false);
    }
  }

  const TOTAL_STEPS = 3;

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <SheetContent className="w-[512px] sm:max-w-[512px] flex flex-col">
        <SheetHeader>
          <SheetTitle>
            Create Account — Step {step} of {TOTAL_STEPS}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          {step === 1 && (
            <>
              <SettingsField label="Account Name">
                <Input
                  value={form.accountName}
                  onChange={(e) => {
                    const name = e.target.value;
                    // ADV-1026: a stale slug-conflict banner must never outlive
                    // an edit — clearing it here keeps the notice in sync with
                    // the (possibly re-derived) slug.
                    if (error) setError(null);
                    if (slugManuallyEdited) {
                      patch({ accountName: name });
                    } else {
                      patch({ accountName: name, accountSlug: slugify(name) });
                    }
                  }}
                  placeholder="Your company name"
                />
              </SettingsField>
              <SettingsField label="Slug">
                <Input
                  value={form.accountSlug}
                  onChange={(e) => {
                    setSlugManuallyEdited(true);
                    if (error) setError(null);
                    patch({ accountSlug: e.target.value });
                  }}
                  placeholder="your-account-slug"
                  className="font-mono"
                />
              </SettingsField>
            </>
          )}

          {step === 2 && (
            <>
              <SettingsField label="Business Name">
                <Input
                  value={form.businessName}
                  onChange={(e) => {
                    const name = e.target.value;
                    // ADV-1026: keep the shared error banner in sync — see the
                    // Account Name handler above.
                    if (error) setError(null);
                    if (businessSlugManuallyEdited) {
                      patch({ businessName: name });
                    } else {
                      patch({
                        businessName: name,
                        businessSlug: slugify(name),
                      });
                    }
                  }}
                  placeholder="Your business name"
                />
              </SettingsField>
              <SettingsField label="Business Slug" description="Optional">
                <Input
                  value={form.businessSlug}
                  onChange={(e) => {
                    setBusinessSlugManuallyEdited(true);
                    if (error) setError(null);
                    patch({ businessSlug: e.target.value });
                  }}
                  placeholder="your-business-slug"
                  className="font-mono"
                />
              </SettingsField>
            </>
          )}

          {step === 3 && (
            <>
              <SettingsField label="Admin Email">
                <Input
                  type="email"
                  value={form.adminEmail}
                  onChange={(e) => patch({ adminEmail: e.target.value })}
                  placeholder="owner@example.com"
                />
              </SettingsField>
              <div className="grid grid-cols-2 gap-3">
                <SettingsField label="First Name">
                  <Input
                    value={form.adminFirstName}
                    onChange={(e) => patch({ adminFirstName: e.target.value })}
                    placeholder="Jane"
                  />
                </SettingsField>
                <SettingsField label="Last Name">
                  <Input
                    value={form.adminLastName}
                    onChange={(e) => patch({ adminLastName: e.target.value })}
                    placeholder="Smith"
                  />
                </SettingsField>
              </div>
            </>
          )}

          {adminExisted && (
            <p className="text-sm text-[color:var(--color-text-md)] border border-[color:var(--color-border)] rounded-md px-3 py-2">
              This user already exists — they&apos;ve been added and notified.
            </p>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <SheetFooter className="border-t pt-4">
          <Button
            variant="outline"
            onClick={() => (step > 1 ? setStep((s) => s - 1) : handleClose())}
          >
            {step > 1 ? "Back" : "Cancel"}
          </Button>
          {adminExisted ? (
            <Button onClick={handleClose}>Done</Button>
          ) : step < TOTAL_STEPS ? (
            <Button
              onClick={() => setStep((s) => s + 1)}
              disabled={step === 2 && !form.businessName.trim()}
            >
              Continue
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={
                submitting ||
                !form.adminEmail.trim() ||
                !form.businessName.trim()
              }
            >
              {submitting ? "Creating..." : "Create Account"}
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

// ─── Accounts Page ────────────────────────────────────────────────────────────

export default function V2AdvaAccountsPage() {
  const { data, isLoading } = useAllAccounts();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);

  const accounts = data?.accounts ?? [];

  return (
    <SettingsPage
      breadcrumbs={[{ label: "Adva" }, { label: "Accounts" }]}
      title="Accounts"
      subtitle="All tenant accounts on the platform."
      actions={
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-1.5" />
          Create Account
        </Button>
      }
    >
      <SettingsTable
        colSpan={7}
        isLoading={isLoading}
        isEmpty={accounts.length === 0}
        emptyMessage="No accounts found."
      >
        <TableHeader>
          <TableRow className="h-9">
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              Name
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              Status
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4 hidden md:table-cell">
              Plan
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              Businesses
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              Users
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4 hidden lg:table-cell">
              Created
            </TableHead>
            <TableHead className="w-24 px-4" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {accounts.map((account) => (
            <TableRow
              key={account.id}
              className="cursor-pointer hover:bg-[color:var(--color-elevated)] transition-colors"
              onClick={() =>
                navigate(`/settings/adva/accounts/${account.id}/users`)
              }
            >
              <TableCell className="px-4 py-2">
                <p className="typo-body font-medium text-[color:var(--color-text-hi)]">
                  {account.name}
                </p>
                <p className="typo-body text-xs font-mono text-[color:var(--color-text-lo)]">
                  {account.slug}
                </p>
              </TableCell>
              <TableCell className="px-4 py-2">
                <Badge variant={statusVariant(account.status)}>
                  {account.status}
                </Badge>
              </TableCell>
              <TableCell className="px-4 py-2 hidden md:table-cell typo-body text-[color:var(--color-text-md)] capitalize">
                {account.plan_type}
              </TableCell>
              <TableCell className="px-4 py-2 typo-body text-[color:var(--color-text-hi)]">
                {account.business_count}
              </TableCell>
              <TableCell className="px-4 py-2 typo-body text-[color:var(--color-text-hi)]">
                {account.user_count}
              </TableCell>
              <TableCell className="px-4 py-2 hidden lg:table-cell typo-body text-xs text-[color:var(--color-text-lo)]">
                {new Date(account.created_at).toLocaleDateString()}
              </TableCell>
              <TableCell className="px-4 py-2">
                <div className="flex items-center gap-1.5">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(
                        `/settings/adva/accounts/${account.id}/businesses`,
                      );
                    }}
                  >
                    <Building2 className="h-3 w-3 mr-1" />
                    Businesses
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/settings/adva/accounts/${account.id}/users`);
                    }}
                  >
                    <Users className="h-3 w-3 mr-1" />
                    Users
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </SettingsTable>

      <CreateAccountSheet
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </SettingsPage>
  );
}
